import React, { useEffect, useState, useMemo } from 'react'
import { Box, Card, Grid, Stack, Typography } from '@mui/material'
import { AxiosError } from 'axios'
import api from '../../services/api'
import { Columns } from './components/Columns'
import { AthleteFormModal } from './components/AthleteFormModal'
import { DataGrid } from './components/DataGrid'
import { Container } from './styles'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { SubHeader } from 'components/SubHeader'
import { initialBreadcrumb } from 'redux/reducers/breadcrumb/actions'
import { useAppDispatch } from 'redux/hooks'
import { useLocation, useNavigate } from 'react-router-dom'
import { DialogConfirmation } from 'components/DialogConfirmation'
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid'
import { DeleteOutline, EditOutlined, FormatListBulleted, Send } from '@mui/icons-material'
import { LoadingPage } from 'components/LoadingPage'
import { showMessage } from 'redux/reducers/snackbar/snackbar.actions'
import useResponsive from 'hooks/useResponsive'
import { DataGridHeader } from './components/DataGridHeader'
import { ProfessionalAthlete } from 'models/interfaces/professional-athlete.interface'
import { Athlete } from 'models/interfaces/athlete.interface'
import { setSummaryProfessionalAthlete } from 'redux/reducers/summary-athlete/actions'
import { TreatEmpty } from 'components/TreatEmpty'
import { TreatEmptySuggestion } from 'components/TreatEmptySuggestion'

const REGISTER_CONFLICT_ERROR_CODE = 'REGISTER_CONFLICT'

export const Athletes = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const location = useLocation()
  const [openConfirmation, setOpenConfirmation] = useState(false)
  const toggleDialog = () => setOpenConfirmation(!openConfirmation)
  const [contentList, setContentList] = useState<ProfessionalAthlete[]>([])
  const [professionalAthleteResult, setProfessionalAthleteResult] = useState<ProfessionalAthlete[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [isVisible, setIsVisible] = useState<boolean>(false)
  const [professionalAthleteToInactivate, setProfessionalAthleteToInactivate] = useState<ProfessionalAthlete>()
  const [professionalAthleteToEdit, setProfessionalAthleteToEdit] = useState<ProfessionalAthlete>()
  const mdUp = useResponsive('up', 'md')
  const [showInstruction, setShowInstruction] = useState<boolean>(false)

  useEffect(() => {
    dataFetch().catch(console.error)
  }, [])

  const dataFetch = async () => {
    dispatch(initialBreadcrumb({ label: t('header.title.athletes'), path: location.pathname }))
    try {
      setLoading(true)
      const { data } = await api.get('professional-athletes?active=true')
      setShowInstruction(data.length > 0 ? false : true)
      setContentList(data)
      setProfessionalAthleteResult(data)
    } catch {
      dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
    } finally {
      setLoading(false)
    }
  }

  const handleAddAthlete = async (athlete: Athlete) => {
    try {
      setLoading(true)
      await api.post('professional-athletes', {
        ...athlete,
        birthday: moment(athlete.birthday).format('YYYY-MM-DD')
      })
      await dataFetch()
      setIsVisible(false)
      dispatch(showMessage({ message: (athlete?.id ? `${t('athlete.update_success')}` : `${t('athlete.create_success')}`) }))
    } catch (e) {
      const errorCode = (e as AxiosError<{ code?: string }>)?.response?.data?.code
      const errorMessage = errorCode === REGISTER_CONFLICT_ERROR_CODE
        ? t('athlete.alreadyPendingOrActive')
        : t('general.error')
      dispatch(showMessage({ message: `${errorMessage}`, type: 'error' }))
    } finally {
      setLoading(false)
    }
    setProfessionalAthleteToEdit(undefined)
  }

  const handleEditAthlete = (data: ProfessionalAthlete) => {
    setProfessionalAthleteToEdit(data)
    setIsVisible(true)
  }

  const handleSearch = (e: any) => {
    if (!e.target.value) {
      setProfessionalAthleteResult(contentList)
      return
    }
    setProfessionalAthleteResult([])
    const dataFound: ProfessionalAthlete[] = []
    const searchText = e.target.value
    const regex = new RegExp(searchText.normalize('NFD').replace(/[\u0300-\u036f]/g, ''), 'i')

    contentList.forEach((obj) => {
      if (obj?.athlete) {
        Object.entries(obj.athlete).forEach(([key]) => {
          if (!dataFound.includes(obj) &&
            (key === 'email' || key === 'genre' || key === 'name' || key === 'surname') &&
            regex.exec(obj.athlete[key].normalize('NFD').replace(/[\u0300-\u036f]/g, ''))) {
            dataFound.push(obj)
          }
        })
      }
    })
    setProfessionalAthleteResult(dataFound)
  }

  const handleOpenRoutines = (data: ProfessionalAthlete) => {
    dispatch(setSummaryProfessionalAthlete(data))
    navigate(`/athletes/${data.id}/routines`)
  }

  // const handleCopyLinkEmail = (linkRegister: string) => {
  //   navigator.clipboard
  //     .writeText(linkRegister)
  //     .then(() => {
  //       dispatch(showMessage({ message: `${t('athlete.copy_link_register_success')}` }))
  //     })
  //     .catch(() => {
  //       dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
  //     })
  // }

  const handleResendInvitation = async (professionalAthlete: ProfessionalAthlete) => {
    try {
      setLoading(true)
      await api.put(`/professional-athletes/${professionalAthlete.id}/resend-invitation`)
      dispatch(showMessage({ message: `${t('athlete.resend_invitation_success')}` }))
    } catch (e) {
      dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
    } finally {
      setLoading(false)
    }
  }

  const handleInactivateProfessionalAthlete = async () => {
    try {
      setLoading(true)
      await api.put(`/professional-athletes/${professionalAthleteToInactivate?.id}/inactivate`)
      await dataFetch()
      dispatch(showMessage({ message: `${t('athlete.delete_success')}` }))
    } catch (e) {
      dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
    } finally {
      setLoading(false)
    }
    setOpenConfirmation(false)
  }

  const deleteAction = (data: ProfessionalAthlete) => {
    setProfessionalAthleteToInactivate(data)
    setOpenConfirmation(true)
  }

  const columns = useMemo<Array<GridColDef<ProfessionalAthlete>>>(
    () => [
      ...Columns,
      {
        field: 'actions',
        type: 'actions',
        width: 100,
        flex: 0.8,
        getActions: (params: any) => params.row?.linkEmail
          ? [
            // <GridActionsCellItem
            // icon={<ContentCopy />}
            // label={t('general.btn.copyLink')}
            // showInMenu
            // onClick={() => handleCopyLinkEmail(params.row?.linkEmail)}/>,
            <GridActionsCellItem
              icon={<Send />}
              label={t('general.btn.sendEmail')}
              showInMenu
              onClick={async () => await handleResendInvitation(params.row)}/>,
            <GridActionsCellItem
              icon={<EditOutlined />}
              label={t('general.btn.edit')}
              showInMenu
              disabled={(params.row?.athlete?.userVerifiedAt ? true : false)}
              onClick={() => handleEditAthlete(params.row)}
            />,
            <GridActionsCellItem
              icon={<FormatListBulleted />}
              label={t('general.btn.routine')}
              showInMenu
              onClick={() => handleOpenRoutines(params.row)} />,
            <GridActionsCellItem sx={{ color: '#d6500d' }}
              icon={<DeleteOutline sx={{ mr: 2 }} color='error'/>}
              label={t('general.btn.delete')}
              showInMenu
              onClick={() => deleteAction(params.row)}
            />]
          : [<GridActionsCellItem
            icon={<EditOutlined />}
            label={t('general.btn.edit')}
            showInMenu
            disabled={(params.row?.athlete?.userVerifiedAt ? true : false)}
            onClick={() => handleEditAthlete(params.row)}/>,
          <GridActionsCellItem
            icon={<FormatListBulleted />}
            label={t('general.btn.routine')}
            showInMenu
            onClick={() => handleOpenRoutines(params.row)} />,
          <GridActionsCellItem sx={{ color: '#d6500d' }}
            icon={<DeleteOutline sx={{ mr: 2 }} color='error'/>}
            label={t('general.btn.delete')}
            showInMenu
            onClick={() => deleteAction(params.row)}
          />]
      }
    ],
    [deleteAction, handleOpenRoutines]
  )

  return (
    <Box>
      <SubHeader
        path={location.pathname}
        goBack={false}
      />
      <Container>
        <DialogConfirmation
          title={`${t('athlete.deleteConfirmation_title')}`}
          message={`${t('athlete.deleteConfirmation_messenger', {
            name: professionalAthleteToInactivate?.athlete?.name,
            surname: professionalAthleteToInactivate?.athlete?.surname
          })}`}
          open={openConfirmation}
          onClose={toggleDialog}
          action={handleInactivateProfessionalAthlete}
          loading={loading}
        />
        <AthleteFormModal
          isVisible={isVisible}
          onClose={() => {
            setIsVisible(false)
            setProfessionalAthleteToEdit(undefined)
          }}
          loading={loading}
          handleAddAthlete={handleAddAthlete}
          professionalAthlete={professionalAthleteToEdit}
        />
        { loading
          ? <LoadingPage />
          : <Stack sx={{ width: '100%' }} >
            <Card sx={{ width: '100%' }} variant="outlined">
              <Stack spacing={2} sx={{ padding: { xs: 0.5, sm: 1 } }}>
                <DataGridHeader
                  onAction={() => setIsVisible(true)}
                  onActionSearch={(value) => handleSearch(value)}
                  onSearchTitle={`${t('search.titleAthlete')}`}
                  onActionTitle={`${t('athlete.title.addAthlete')}`}
                />
                {professionalAthleteResult.length === 0 && contentList.length > 0
                  ? <TreatEmpty subTitleMessage={t('general.searchEmpty')} />
                  : contentList && contentList.length === 0
                    ? <TreatEmptySuggestion
                      textSuggestion={[
                        t('athlete.title.suggestion_1', { addAthlete: t('athlete.title.addAthlete') }),
                        t('athlete.title.suggestion_2')
                      ]}
                      showInstruction={showInstruction}
                    />
                    : <DataGrid
                      rows={professionalAthleteResult}
                      onRowClick={(params) => {
                        handleOpenRoutines(params.row)
                      }}
                      initialState={{
                        sorting: {
                          sortModel: [{ field: 'name', sort: 'asc' }]
                        }
                      }}
                      columns={mdUp
                        ? columns
                        : columns.filter(
                          (column) => (
                            column.field !== 'birthday' &&
                            column.field !== 'whatsapp' &&
                            column.field !== 'genre'
                          )
                        )
                      }
                      loading={loading}
                      components={{
                        NoRowsOverlay: () => (
                          <Grid container justifyContent="center" alignItems="center" spacing={2} p={5}>
                            <Grid item xs={12} textAlign="center" sx={{ marginTop: 5 }}>
                              <Typography variant='body1' alignItems="center">
                                {t('search.notFound')}
                              </Typography>
                            </Grid>
                          </Grid>
                        )
                      }}
                      sx={{
                        '&.MuiDataGrid-root .css-2ovxb4-MuiContainer-root': {
                          paddingLeft: 1
                        },
                        '.MuiDataGrid-cell:focus': {
                          outline: 'none'
                        },
                        '& .MuiDataGrid-row:hover': {
                          cursor: 'pointer'
                        }
                      }}
                    />
                }
              </Stack>
            </Card>
          </Stack>
        }
      </Container>
    </Box>
  )
}
